import { CartAddEvent } from '@theme/events';

const pendingProducts = new Set();
const networkError = (label) => `No pudimos añadir ${label}. Inténtalo nuevamente.`;
const uncertainError = 'No pudimos confirmar el carrito. Revísalo antes de volver a intentar.';

class ImperiaPackUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-pack-add]');
    this.status = this.querySelector('[data-pack-status]');
    if (!this.button || !this.status) return;

    this.defaultButtonText = this.button.textContent.trim();
    this.handleClick = this.handleClick.bind(this);
    this.button.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  async readCart() {
    const response = await fetch(this.dataset.cartUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart read failed');

    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
    return cart;
  }

  hasAccessory(cart) {
    const productIds = this.dataset.blockingProductIds
      ? this.dataset.blockingProductIds.split(',').filter(Boolean)
      : [this.dataset.productId, this.dataset.caseProductId, this.dataset.strapProductId];
    return cart.items.some((item) => productIds.includes(String(item.product_id)));
  }

  hasDrawerEyewear(cart) {
    if (this.dataset.context !== 'drawer' || this.dataset.eyewearProductIds === undefined) return true;
    const eyewearIds = (this.dataset.eyewearProductIds || '').split(',').filter(Boolean);
    return cart.items.some((item) => eyewearIds.includes(String(item.product_id)));
  }

  setBusy(isBusy) {
    const statusLabel = this.dataset.statusLabel || 'el pack';
    this.button.disabled = isBusy;
    this.setAttribute('aria-busy', String(isBusy));
    this.button.textContent = isBusy ? 'AÑADIENDO…' : this.defaultButtonText;
    this.status.textContent = isBusy ? `Añadiendo ${statusLabel} al carrito…` : '';
  }

  finishAdd(data, itemCount) {
    document.dispatchEvent(new CartAddEvent({}, this.dataset.variantId, {
      source: 'imperia-pack-upsell',
      productId: this.dataset.productId,
      itemCount,
      sections: data.sections || {},
    }));

    document.querySelectorAll('imperia-pack-upsell').forEach((upsell) => {
      if (upsell.dataset.productId === this.dataset.productId) upsell.hidden = true;
    });
  }

  async handleClick() {
    const productId = this.dataset.productId;
    const statusLabel = this.dataset.statusLabel || 'el pack';
    if (!productId || pendingProducts.has(productId)) return;
    pendingProducts.add(productId);
    this.setBusy(true);

    let addStarted = false;
    try {
      const cart = await this.readCart();
      if (this.hasAccessory(cart) || !this.hasDrawerEyewear(cart)) {
        this.hidden = true;
        return;
      }

      const sectionIds = [...new Set(
        Array.from(document.querySelectorAll('cart-items-component'))
          .map((component) => component.dataset.sectionId)
          .filter(Boolean)
      )];
      const body = {
        items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };

      addStarted = true;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        this.status.textContent = data.description || data.message || networkError(statusLabel);
        return;
      }

      let itemCount = Number(cart.item_count) + 1;
      try {
        itemCount = Number((await this.readCart()).item_count);
      } catch {
        // The add response succeeded; use the previous count if the follow-up read fails.
      }
      this.finishAdd(data, itemCount);
    } catch (error) {
      if (addStarted) {
        try {
          const cart = await this.readCart();
          if (cart.items.some((item) => String(item.product_id) === productId)) {
            this.finishAdd({}, Number(cart.item_count));
            return;
          }
          this.status.textContent = networkError(statusLabel);
        } catch {
          this.status.textContent = uncertainError;
        }
      } else {
        this.status.textContent = networkError(statusLabel);
      }
      console.error('IMPERIA pack upsell:', error);
    } finally {
      pendingProducts.delete(productId);
      this.button.disabled = false;
      this.removeAttribute('aria-busy');
      this.button.textContent = this.defaultButtonText;
    }
  }
}

if (!customElements.get('imperia-pack-upsell')) {
  customElements.define('imperia-pack-upsell', ImperiaPackUpsell);
}
